import DeliveryTypeSelect from './DeliveryTypeSelect';
import { useEffect, useRef, useState } from '@wordpress/element';
import { __ } from '@wordpress/i18n';
import { VendorDeliveryTimeData, VendorInfo } from '../types/DeliveryTIme';
import apiFetch from '@wordpress/api-fetch';
// @ts-ignore
const { addQueryArgs } = wp.url;
// @ts-ignore
const { SelectControl } = wp.components;

const DeliveryTimeItem = ( {
    vendorId,
    info,
    onChange,
    values,
    setLoading,
}: {
    vendorId: string;
    info: VendorInfo;
    onChange: ( data: object ) => void;
    values: VendorDeliveryTimeData;
    setLoading: ( value: boolean ) => void;
} ) => {
    const datePicker = useRef( null );

    const [ data, setData ] = useState( {
        vendor_id: vendorId,
        store_name: info?.store_name ?? '',
        delivery_date: values?.delivery_date ?? '',
        selected_delivery_type: values?.selected_delivery_type ?? 'delivery',
        delivery_time_slot: values?.delivery_time_slot ?? '',
        store_pickup_location: values?.store_pickup_location ?? '',
        timeSlots: values?.timeSlots ?? [],
        locationSlots: values?.locationSlots ?? [],
    } );

    const initCalendar = () => {
        const config = {
            disable: [],
            minDate: 'today',
            altInput: true,
            // @ts-ignore
            altFormat: window.dokan_helper.i18n_date_format.replace(
                'jS',
                'J'
            ),
            dateFormat: 'Y-m-d',
            // @ts-ignore
            locale: dokan_flatpickr_i18n,
            disableDays: [],
            onChange: onDateChange,
        };

        let deliveryDays = [];
        // Collect vendor ids.
        const allDeliveryDays = [
            'sunday',
            'monday',
            'tuesday',
            'wednesday',
            'thursday',
            'friday',
            'saturday',
        ];

        const vendorDeliveryDayObj = info.vendor_delivery_options?.delivery_day,
            vendorDeliveryDays =
                typeof vendorDeliveryDayObj !== 'undefined'
                    ? Object.entries( vendorDeliveryDayObj )
                          // @ts-ignore
                          .map( ( dayArray ) =>
                              parseInt( dayArray[ 1 ] ) ||
                              dayArray[ 0 ] === dayArray[ 1 ]
                                  ? dayArray[ 0 ]
                                  : ''
                          )
                          .filter( ( day ) => ( day !== null ? day : '' ) )
                    : [];

        // If vendor delivery days not exists.
        if ( ! vendorDeliveryDays.length ) {
            return;
        }

        deliveryDays = vendorDeliveryDays;

        const deliveryBlockedDaysIndex = [];
        allDeliveryDays.forEach( ( day ) => {
            if ( ! deliveryDays.includes( day ) ) {
                deliveryBlockedDaysIndex.push( allDeliveryDays.indexOf( day ) );
            }
        } );

        config.disableDays = [
            function ( date ) {
                // Return true if disabled.
                return deliveryBlockedDaysIndex.includes( date.getDay() );
            },
        ];

        config.disable = [ ...config.disableDays ];
        const vendorVacationDays = info.vendor_vacation_days,
            preOrderBlockedDates = info.vendor_preorder_blocked_dates;

        config.disable = [
            ...config.disableDays,
            ...vendorVacationDays,
            ...preOrderBlockedDates,
        ];

        delete config.disableDays;
        // @ts-ignore
        window.flatpickr( datePicker.current, config );
    };

    useEffect( () => {
        initCalendar();
    }, [] );

    const setDataHandler = ( data ) => {
        setData( data );
        onChange( data );
    };

    const fetchTimeSlot = ( values ) => {
        setLoading( true );
        return apiFetch( {
            path: addQueryArgs( '/dokan/v1/delivery-time/time-slot', {
                vendorId: values.vendor_id,
                date: values.delivery_date,
            } ),
        } )
            .then( ( res ) => {
                if ( 'object' === typeof res ) {
                    const resValues = {
                        ...values,
                    };
                    resValues.delivery_time_slot = '';
                    resValues.timeSlots = Object.keys( res );

                    setData( resValues );
                    onChange( resValues );

                    return resValues;
                }

                throw new Error( 'Invalid response format' );
            } )
            .finally( () => {
                setLoading( false );
            } );
    };

    const fetchLocationSlot = ( values ) => {
        setLoading( true );

        return apiFetch( {
            path: addQueryArgs( '/dokan/v1/delivery-time/location-slot', {
                vendorId: values.vendor_id,
            } ),
        } )
            .then( ( res ) => {
                if ( 'object' === typeof res ) {
                    const resValues = {
                        ...values,
                    };
                    resValues.locationSlots = Object.values( res );

                    setData( resValues );
                    onChange( resValues );

                    return resValues;
                }

                throw new Error( 'Invalid response format' );
            } )
            .finally( () => {
                setLoading( false );

                return true;
            } );
    };

    function onDateChange( selectedDates, dateStr, instance ) {
        // eslint-disable-next-line @typescript-eslint/no-shadow
        const values = { ...data, delivery_date: dateStr };

        fetchLocationSlot( values ).then( ( res ) => {
            fetchTimeSlot( res );
        } );
    }

    const getStoreLocationInfoFromFormattedString = ( address ) => {
        if ( ! address ) {
            return {
                name: '',
                address: '',
            };
        }

        const location_name = address.split( '(' )[ 0 ];
        const location_address = address.match( /\((.*?)\)/ )[ 1 ];

        return {
            name: location_name,
            address: location_address,
        };
    };

    const handleDeliveryType = ( value ) => {
        const updatedData = { ...data, selected_delivery_type: value };

        fetchLocationSlot( updatedData ).then( ( resDate ) => {
            setDataHandler( resDate );
        } );
    };

    const getTimes = () => {
        const times = data.timeSlots.map( ( item ) => {
            return {
                label: item,
                value: item,
            };
        } );

        times.push( {
            label: __( 'Select time slot', 'dokan' ),
            value: '',
        } );

        return times;
    };

    const getLocations = () => {
        const locationSlots = data.locationSlots.map( ( item ) => {
            return {
                label: getStoreLocationInfoFromFormattedString( item ).name,
                value: item,
            };
        } );

        locationSlots.push( {
            label: __( 'Select store location', 'dokan' ),
            value: '',
        } );

        return locationSlots;
    };

    return (
        <div className="delivery-group">
            <div className="title">
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    height="22"
                    viewBox="0 0 24 24"
                    stroke="#333333"
                >
                    <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"
                    ></path>
                </svg>
                <span>{ info?.store_name ?? '' }</span>
            </div>

            <div className="vendor-info">
                <span>
                    { info?.vendor_delivery_options
                        ?.delivery_box_info_message ?? '' }
                </span>
            </div>

            <DeliveryTypeSelect
                value={ data?.selected_delivery_type }
                onClick={ handleDeliveryType }
                isDeliveryEnabled={ info?.is_delivery_time_active }
                isPickupEnabled={ info?.is_store_location_active }
            />

            <input
                defaultValue={ data?.delivery_date || '' }
                readOnly
                ref={ datePicker }
                className="delivery-time-date-picker flatpickr-input"
                type="text"
                placeholder={
                    info?.vendor_delivery_options?.delivery_date_label ??
                    __( 'Delivery Date', 'dokan' )
                }
            />

            { Number( data.timeSlots.length ) > 0 && (
                <SelectControl
                    __next40pxDefaultSize
                    __nextHasNoMarginBottom
                    onChange={ ( value ) =>
                        setDataHandler( {
                            ...data,
                            delivery_time_slot: value,
                        } )
                    }
                    className="delivery-time-vendor-select"
                    value={ data.delivery_time_slot ?? '' }
                    options={ getTimes() }
                />
            ) }

            { data.locationSlots.length > 0 &&
                data.selected_delivery_type === 'pickup' && (
                    <div className="store-pickup-select-options">
                        <SelectControl
                            __next40pxDefaultSize
                            __nextHasNoMarginBottom
                            onChange={ ( value ) =>
                                setDataHandler( {
                                    ...data,
                                    store_pickup_location: value,
                                } )
                            }
                            className="delivery-time-vendor-select"
                            value={ data.store_pickup_location ?? '' }
                            options={ getLocations() }
                        />

                        { data.store_pickup_location && (
                            <div className="store-address vendor-info">
                                <span id="delivery-store-location-address-3">
                                    {
                                        getStoreLocationInfoFromFormattedString(
                                            data.store_pickup_location
                                        ).address
                                    }
                                </span>
                            </div>
                        ) }
                    </div>
                ) }
            <hr />
        </div>
    );
};

export default DeliveryTimeItem;
